// Workspace sidebar (premium AI OS feel — ChatGPT / Claude / Linear / Cursor / Raycast).
// Sections (top → bottom):
//   1. Logo + collapse toggle
//   2. New Chat
//   3. Search
//   4. AI Tools (existing tools list)
//   5. Projects (collapsible folders w/ nested conversations + ⋮ menu)
//   6. Recent Chats (conversations with no project + ⋮ menu)
//   7. User profile (pinned bottom) + theme toggle + sign in/out
import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Plus, Search, ChevronLeft, ChevronRight, FolderPlus, Bell,
  Wand2, FileText, Brain, History, MessageSquare, Lock,
  Settings as SettingsIcon, LogOut, User,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ThemeToggle } from "@/components/ThemeToggle";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import { supabase } from "@/lib/supabase";
import { TOOLS, knowledgeFilledCount } from "@/lib/workspaceData";
import { ProjectItem } from "./ProjectItem";
import { ConversationItem } from "./ConversationItem";

const TOOL_ICON = {
  reply_assistant: Wand2,
  proposal_generator: FileText,
  client_analyzer: Brain,
  follow_up: Bell,
  history: History,
};

function SectionLabel({ children, right }) {
  return (
    <div className="flex items-center justify-between px-2 pt-1">
      <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-muted-foreground/80">
        {children}
      </p>
      {right}
    </div>
  );
}

export function Sidebar({
  collapsed, setCollapsed, activeId, currentTool,
  data,                           // useWorkspaceData() return value
  query, setQuery,
  onNewChat, onPickTool, onPickConversation,
  onCreateProject, onOpenKnowledge,
  onDeleteConversation,           // branded confirm handler from Workspace
  onDeleteProject,                // branded confirm handler from Workspace
  isLockedForUser,                // (toolId) => bool
}) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [creatingProject, setCreatingProject] = useState(false);
  const [draftProjectName, setDraftProjectName] = useState("");

  const q = query.trim().toLowerCase();

  // Search filters conversations. Projects are kept if name matches OR
  // any of their conversations match.
  const filteredConvIds = useMemo(() => {
    if (!q) return null;
    return new Set(
      data.conversations
        .filter((c) => c.title.toLowerCase().includes(q))
        .map((c) => c.id)
    );
  }, [q, data.conversations]);

  const visibleProjects = useMemo(() => {
    if (!q) return data.projects;
    return data.projects.filter((p) => {
      if (p.name.toLowerCase().includes(q)) return true;
      const kids = data.conversationsByProject[p.id] || [];
      return kids.some((c) => filteredConvIds.has(c.id));
    });
  }, [q, data.projects, data.conversationsByProject, filteredConvIds]);

  const visibleOrphans = useMemo(() => {
    if (!q) return data.orphanConversations;
    return data.orphanConversations.filter((c) => filteredConvIds.has(c.id));
  }, [q, data.orphanConversations, filteredConvIds]);

  const commitNewProject = async () => {
    const name = draftProjectName.trim();
    setCreatingProject(false);
    setDraftProjectName("");
    if (!name) return;
    try { await onCreateProject(name); } catch (e) { console.error(e); }
  };

  return (
    <aside
      data-testid="workspace-sidebar"
      className={[
        "flex h-screen flex-col border-r border-border bg-card/40 transition-[width] duration-200 ease-out",
        collapsed ? "w-[60px]" : "w-[280px]",
      ].join(" ")}
    >
      {/* Top: logo + collapse */}
      <div className={`flex items-center gap-2 px-3 pt-3 ${collapsed ? "justify-center" : "justify-between"}`}>
        {!collapsed && <Logo />}
        <Button
          variant="ghost" size="icon"
          data-testid="sidebar-toggle"
          onClick={() => setCollapsed((v) => !v)}
          className="h-8 w-8 rounded-md text-muted-foreground hover:text-foreground"
          aria-label="Toggle sidebar"
        >
          {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
        </Button>
      </div>

      {/* New chat */}
      <div className="px-3 pt-4">
        <Button
          data-testid="new-chat-btn"
          onClick={() => onNewChat()}
          title={collapsed ? "New chat" : undefined}
          className={[
            "h-9 w-full rounded-lg bg-primary text-[13px] font-medium text-primary-foreground shadow-sm shadow-primary/20 hover:bg-primary/90",
            collapsed ? "justify-center px-0" : "justify-start gap-2",
          ].join(" ")}
        >
          <Plus className="h-4 w-4" />
          {!collapsed && <span>New chat</span>}
        </Button>
      </div>

      {/* Search */}
      {!collapsed && (
        <div className="px-3 pt-3">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input
              data-testid="search-chats-input"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search conversations…"
              className="h-9 rounded-lg border-border bg-background pl-8 text-[12.5px]"
            />
          </div>
        </div>
      )}

      {/* Scrollable middle */}
      <div className="mt-4 flex-1 overflow-y-auto px-3 pb-3">
        {/* AI Tools */}
        {!collapsed && (
          <SectionLabel>AI Tools</SectionLabel>
        )}
        <ul className="mt-1 space-y-0.5">
          {TOOLS.map((t) => {
            const Icon = TOOL_ICON[t.id];
            const isActiveTool = currentTool === t.id && !activeId;
            const locked = isLockedForUser?.(t.id);
            return (
              <li key={t.id}>
                <button
                  data-testid={`tool-pick-${t.id}`}
                  onClick={() => onPickTool(t.id)}
                  title={collapsed ? t.label : undefined}
                  className={[
                    "flex w-full items-center rounded-md py-1.5 text-[12.5px] transition-colors",
                    collapsed ? "justify-center px-0" : "gap-2 px-2",
                    isActiveTool
                      ? "bg-secondary text-foreground"
                      : "text-muted-foreground hover:bg-secondary/60 hover:text-foreground",
                  ].join(" ")}
                >
                  <Icon className="h-3.5 w-3.5 shrink-0" />
                  {!collapsed && (
                    <>
                      <span className="truncate">{t.label}</span>
                      {locked && (
                        <span
                          className="ml-auto inline-flex items-center gap-0.5 rounded-full bg-secondary px-1.5 py-0.5 text-[9.5px] uppercase text-muted-foreground"
                          title="Sign up to unlock"
                        >
                          <Lock className="h-2.5 w-2.5" /> Pro
                        </span>
                      )}
                    </>
                  )}
                </button>
              </li>
            );
          })}
          {!collapsed && (
            <li>
              <div className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-[12.5px] text-muted-foreground/60">
                <Bell className="h-3.5 w-3.5 shrink-0" />
                <span className="truncate">Follow-up Generator</span>
                <span className="ml-auto rounded-full bg-secondary px-1.5 py-0.5 text-[9.5px] uppercase">Soon</span>
              </div>
            </li>
          )}
        </ul>

        {/* Projects */}
        {!collapsed && (
          <>
            <div className="mt-5">
              <SectionLabel
                right={
                  <button
                    data-testid="new-project-btn"
                    onClick={() => { setCreatingProject(true); setDraftProjectName(""); }}
                    className="rounded p-1 text-muted-foreground hover:bg-secondary hover:text-foreground"
                    aria-label="New project"
                  >
                    <FolderPlus className="h-3.5 w-3.5" />
                  </button>
                }
              >
                Projects
              </SectionLabel>
            </div>
            <ul className="mt-1 space-y-0.5">
              {creatingProject && (
                <li className="rounded-md px-1.5">
                  <div className="flex items-center gap-2 py-1.5">
                    <FolderPlus className="h-3.5 w-3.5 shrink-0 text-amber-400/80" />
                    <input
                      autoFocus
                      data-testid="new-project-input"
                      value={draftProjectName}
                      onChange={(e) => setDraftProjectName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") { e.preventDefault(); commitNewProject(); }
                        else if (e.key === "Escape") { e.preventDefault(); setCreatingProject(false); }
                      }}
                      onBlur={commitNewProject}
                      placeholder="Project name…"
                      className="w-full rounded-md border border-primary/50 bg-background px-1.5 py-0.5 text-[12.5px] text-foreground outline-none ring-2 ring-primary/20"
                    />
                  </div>
                </li>
              )}
              {visibleProjects.length === 0 && !creatingProject && (
                <li className="px-2 py-1.5 text-[11.5px] text-muted-foreground/70">
                  {q ? "No matching projects" : "No projects yet"}
                </li>
              )}
              {visibleProjects.map((p) => {
                const kids = data.conversationsByProject[p.id] || [];
                const filteredKids = q
                  ? kids.filter((c) => filteredConvIds.has(c.id))
                  : kids;
                const isCollapsed = !!data.collapsedMap[p.id];
                return (
                  <li key={p.id}>
                    <ProjectItem
                      project={p}
                      collapsed={isCollapsed}
                      count={kids.length}
                      knowledgeCount={knowledgeFilledCount(p.knowledge)}
                      onToggle={data.toggleProjectCollapsed}
                      onRename={data.updateProject}
                      onDelete={onDeleteProject || data.removeProject}
                      onOpenKnowledge={onOpenKnowledge}
                    >
                      {filteredKids.length === 0 ? (
                        <p className="px-6 py-1 text-[11px] text-muted-foreground/60">
                          {q ? "No matches in this project" : "Drop conversations here"}
                        </p>
                      ) : (
                        filteredKids.map((c) => (
                          <ConversationItem
                            key={c.id}
                            conv={c}
                            isActive={c.id === activeId}
                            projects={data.projects}
                            onPick={onPickConversation}
                            onRename={data.updateConversation}
                            onMove={data.moveConversationToProject}
                            onDelete={onDeleteConversation || data.removeConversation}
                            nested
                          />
                        ))
                      )}
                    </ProjectItem>
                  </li>
                );
              })}
            </ul>

            {/* Recent Chats */}
            <div className="mt-5">
              <SectionLabel>Recent Chats</SectionLabel>
            </div>
            <ul className="mt-1 space-y-0.5">
              {visibleOrphans.length === 0 && (
                <li className="px-2 py-1.5 text-[11.5px] text-muted-foreground/70">
                  {q ? "No matching conversations" : "No conversations yet"}
                </li>
              )}
              {visibleOrphans.map((c) => (
                <li key={c.id}>
                  <ConversationItem
                    conv={c}
                    isActive={c.id === activeId}
                    projects={data.projects}
                    onPick={onPickConversation}
                    onRename={data.updateConversation}
                    onMove={data.moveConversationToProject}
                    onDelete={onDeleteConversation || data.removeConversation}
                  />
                </li>
              ))}
            </ul>
          </>
        )}
      </div>

      {/* Bottom: user profile + theme + sign in/out */}
      <div className={`border-t border-border p-3 ${collapsed ? "flex flex-col items-center gap-2" : "space-y-2"}`}>
        {!collapsed && (
          <div className="flex items-center gap-2 rounded-lg border border-border bg-background/60 p-2">
            <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-primary/15 text-primary">
              <User className="h-3.5 w-3.5" />
            </span>
            <div className="min-w-0 flex-1">
              <p className="truncate text-[12px] font-medium text-foreground">
                {user?.email || "Anonymous"}
              </p>
              <p className="text-[10.5px] text-muted-foreground">
                {user ? "Free plan" : "Try free · sign in to save"}
              </p>
            </div>
          </div>
        )}
        <div className={collapsed ? "flex flex-col items-center gap-1" : "flex items-center justify-between"}>
          <ThemeToggle />
          {user ? (
            <Button
              variant="ghost" size="icon"
              data-testid="sidebar-signout-btn"
              onClick={() => supabase.auth.signOut()}
              className="h-8 w-8 rounded-md text-muted-foreground hover:text-foreground"
              aria-label="Sign out"
            >
              <LogOut className="h-4 w-4" />
            </Button>
          ) : (
            <Button asChild variant="ghost" size="icon"
              className="h-8 w-8 rounded-md text-muted-foreground hover:text-foreground"
              aria-label="Sign in"
            >
              <Link to="/login?next=/workspace"><User className="h-4 w-4" /></Link>
            </Button>
          )}
          <Button asChild variant="ghost" size="icon"
            className="h-8 w-8 rounded-md text-muted-foreground hover:text-foreground"
            aria-label="Settings"
          >
            <Link to="/settings"><SettingsIcon className="h-4 w-4" /></Link>
          </Button>
        </div>
      </div>
    </aside>
  );
}
